<template>
  <div class="quillContainer">
    <div id="toolbar">
      <t-popup :content="$t('粗体')" show-arrow>
        <button class="ql-bold" :title="$t('粗体')"></button>
      </t-popup>
      <t-popup :content="$t('图片')" show-arrow>
        <button class="ql-image" :title="$t('图片')"></button>
      </t-popup>
      <t-popup :content="$t('链接')" show-arrow>
        <button class="ql-link" :title="$t('链接')"></button>
      </t-popup>
      <t-popup :content="$t('视频')" show-arrow>
        <button class="ql-video-custom" :title="$t('视频')">
          <svg viewBox="0 0 18 18" width="18" height="18">
            <rect class="ql-stroke" height="12" width="12" x="3" y="3"></rect>
            <path class="ql-fill" d="M 6 5 L 12 9 L 6 13 z"></path>
          </svg>
        </button>
      </t-popup>
    </div>
    <div id="richContainer" class="richZh"></div>
    <add-link-dialog ref="addLink" @submit="handleToolbarAddLink" />

    <!-- 视频插入对话框 -->
    <t-dialog
      v-model:visible="videoDialogVisible"
      :header="$t('插入视频')"
      width="500px"
      :footer="false"
    >
      <t-form>
        <t-form-item :label="$t('视频URL')">
          <t-input
            v-model="videoUrl"
            :placeholder="
              $t('请输入视频地址（如：https://example.com/video.mp4）')
            "
          />
        </t-form-item>
      </t-form>
      <div style="margin-top: 24px; text-align: right">
        <t-button
          theme="primary"
          shape="round"
          style="margin-right: 8px"
          @click="handleInsertVideo"
        >
          {{ $t("完成") }}
        </t-button>
        <t-button
          theme="default"
          shape="round"
          @click="videoDialogVisible = false"
        >
          {{ $t("取消") }}
        </t-button>
      </div>
    </t-dialog>
  </div>
</template>

<script lang="ts" setup>
import { ref, onMounted, onUnmounted, inject, Ref } from "vue";
import Quill from "quill";
import ImageUploader from "quill-image-uploader";
import { Delta } from "quill/core";
import BoldIcon from "./img/bold.svg";
import ImageIcon from "./img/image.svg";
import LinkIcon from "./img/link.svg";
import { trim } from "lodash-es";
import turndownService from "@/utils/turndownService";
import DOMPurify from "dompurify";
import { MessagePlugin } from "tdesign-vue-next";

const emit = defineEmits(["updateContent", "uploadImg"]);

// 定义自定义图标
const customIcons = {
  bold: `<img src="${BoldIcon}" alt="Bold">`,
  image: `<img src="${ImageIcon}" alt="Image">`,
  link: `<img src="${LinkIcon}" alt="Link">`,
};

const uploadCosFn = inject<Ref<(file: File) => Promise<string>>>("uploadCos");

console.log("uploadCosFn----", uploadCosFn);

const props = defineProps<{
  value: string;
}>();

const addLink = ref<{ open: (text: string) => void; close: () => void }>();
const videoDialogVisible = ref(false);
const videoUrl = ref("");
let quill: Quill | null = null;

// 创建自定义 Video Blot 以支持 video 标签
const BlockEmbed = Quill.import("blots/block/embed") as any;
class VideoBlot extends BlockEmbed {
  static blotName = "video";
  static tagName = "video";

  static create(value: string) {
    const node = super.create() as HTMLVideoElement;

    // 如果 value 是完整的 HTML，解析并设置
    if (value.includes("<video")) {
      // 使用 DOMPurify 清理 HTML，防止 XSS 攻击
      const cleanHtml = DOMPurify.sanitize(value, {
        ALLOWED_TAGS: ["video", "source"],
        ALLOWED_ATTR: [
          "src",
          "type",
          "controls",
          "width",
          "height",
          "autoplay",
          "loop",
          "muted",
          "poster",
          "preload",
          "style",
        ],
        ALLOW_DATA_ATTR: false,
      });

      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = cleanHtml;
      const sourceVideo = tempDiv.querySelector("video");
      if (sourceVideo) {
        // 复制所有属性
        Array.from(sourceVideo.attributes).forEach((attr) => {
          node.setAttribute(attr.name, attr.value);
        });
        // 复制所有子元素（如 source 标签）
        node.innerHTML = sourceVideo.innerHTML;
      }
    } else {
      node.setAttribute("src", value);
      node.setAttribute("controls", "");
    }

    node.style.maxWidth = "100%";
    node.style.display = "block";
    // 添加视觉提示，让用户知道可以点击选中
    node.style.cursor = "pointer";

    return node;
  }

  static value(node: HTMLVideoElement) {
    return node.outerHTML;
  }
}

Quill.register(VideoBlot);
Quill.register("modules/imageUploader", ImageUploader);
(Quill as any).import("ui/icons").bold = customIcons.bold;
(Quill as any).import("ui/icons").image = customIcons.image;
(Quill as any).import("ui/icons").link = customIcons.link;

const moduleConfig = {
  toolbar: {
    container: "#toolbar",
    handlers: {
      "video-custom": () => {
        // 打开视频插入对话框
        videoDialogVisible.value = true;
      },
    },
  },
  imageUploader: {
    upload: async (file: File) => {
      if (uploadCosFn?.value) {
        try {
          const res = await uploadCosFn.value(file); // 调用父组件的方法
          console.log("Upload Result:", res);

          // 粘贴或拖入图片时 删除base64 img
          const range = quill?.getSelection();
          if (range) {
            const [leaf] = quill?.getLeaf(range.index) ?? [];
            if (
              leaf?.domNode &&
              (leaf.domNode as any).tagName === "IMG" &&
              (leaf.domNode as any).src.startsWith("data:")
            ) {
              quill?.deleteText(range.index - 1, 1, "user");
            }
          }

          return res; // 返回图片的 URL
        } catch (error: any) {
          throw new Error("上传失败");
        }
      }
      return "";
    },
  },
  clipboard: {
    matchers: [
      [
        "video",
        (node: HTMLElement, delta: any) => {
          const videoHtml = node.outerHTML;
          return {
            ops: [{ insert: { video: videoHtml } }],
          };
        },
      ],
    ],
  },
};

const handleLinkClick = () => {
  const range = quill?.getSelection(true);
  const selectedText = quill?.getText(range?.index, range?.length);
  if (addLink.value) {
    addLink.value.open?.(selectedText || "");
  }
};

const handleToolbarAddLink = ({ linkText, linkUrl }) => {
  try {
    if (linkUrl && quill) {
      // 获取当前编辑器的内容
      // const currentContents = quill.value.getContents();
      // 获取当前光标位置的范围
      const range = quill?.getSelection(true);
      const linkDelta = new Delta()
        .retain(range.index)
        .delete(range.length)
        .insert(linkText, { link: linkUrl });
      quill.setSelection(null as any);
      quill.updateContents(linkDelta, "user");
      // 设置光标位置和选区
      const newIndex = range.index + linkText.length;
      quill.setSelection(newIndex, 0);
    }
  } catch (error) {
    console.log(error);
  }
};

// 插入视频
const handleInsertVideo = () => {
  if (!videoUrl.value || !quill) {
    return;
  }

  // 验证 URL，只允许 http(s) 协议，防止 javascript: 等危险协议
  let sanitizedUrl = "";
  try {
    const urlObj = new URL(videoUrl.value);
    if (["http:", "https:"].includes(urlObj.protocol)) {
      sanitizedUrl = videoUrl.value;
    } else {
      MessagePlugin.warning("不支持的协议类型，仅支持 http 和 https");
      return;
    }
  } catch {
    MessagePlugin.warning("无效的 URL 格式，请输入正确的视频地址");
    return;
  }

  // 构建 video HTML，使用自适应宽度
  const videoHtml = `<video controls style="max-width: 100%; height: auto;">
  <source src="${sanitizedUrl}" type="video/mp4">
</video>`;

  // 获取当前光标位置
  const range = quill.getSelection(true);

  // 插入 video
  quill.insertEmbed(range.index, "video", videoHtml, "user");

  // 移动光标到视频后面
  quill.setSelection(range.index + 1, 0);

  // 重置表单
  videoUrl.value = "";
  videoDialogVisible.value = false;
};

onMounted(() => {
  const _quill = new Quill("#richContainer", {
    theme: "snow",
    modules: moduleConfig,
    readOnly: false,
    bounds: ".flow-t-drawer__body",
  });

  _quill.setContents(_quill.clipboard.convert({ html: props.value }));

  const toolbar: any = _quill.getModule("toolbar");
  toolbar.addHandler("link", handleLinkClick);
  _quill.on("editor-change", () => {
    emit(
      "updateContent",
      turndownService.turndown(trim(quill?.root.innerHTML)),
    );
  });

  // 点击视频时选中它，解决只有视频时无法删除的问题
  _quill.root.addEventListener("click", (e: MouseEvent) => {
    const target = e.target as HTMLElement;
    // 检查点击的是否是 video 元素或其子元素
    const videoElement = target.closest("video");
    if (videoElement && _quill) {
      // 获取 video 元素对应的 blot
      const blot = Quill.find(videoElement) as any;
      if (blot && typeof blot.offset === "function") {
        const index = _quill.getIndex(blot);
        // 选中整个视频（长度为1）
        _quill.setSelection(index, 1, "user");
        // 添加选中样式
        _quill.root.querySelectorAll("video.selected").forEach((v) => {
          v.classList.remove("selected");
        });
        videoElement.classList.add("selected");
      }
    } else {
      // 点击其他地方时清除选中样式
      _quill.root.querySelectorAll("video.selected").forEach((v) => {
        v.classList.remove("selected");
      });
    }
  });

  quill = _quill;
});

// 键盘事件处理函数 - 处理视频删除
const handleKeyDown = (e: KeyboardEvent) => {
  if (!quill) return;

  // 检查是否按下删除键
  if (e.key === "Delete" || e.key === "Backspace") {
    // 方案1：检查选区长度为1的情况（视频后有其他内容时）
    const selection = quill.getSelection();
    if (selection && selection.length === 1) {
      // 检查选中的是否是视频
      const [blot] = quill.getLeaf(selection.index);
      if (blot && (blot as any).statics?.blotName === "video") {
        e.preventDefault();
        quill.deleteText(selection.index, 1, "user");
        // 清除选中样式
        quill.root.querySelectorAll("video.selected").forEach((v) => {
          v.classList.remove("selected");
        });
        return;
      }
    }

    // 方案2：检查是否有被选中的视频元素（视频是唯一内容时，选区长度可能为0）
    const selectedVideo = quill.root.querySelector("video.selected");
    if (selectedVideo) {
      const blot = Quill.find(selectedVideo) as any;
      if (blot && typeof blot.offset === "function") {
        const index = quill.getIndex(blot);
        e.preventDefault();
        quill.deleteText(index, 1, "user");
        selectedVideo.classList.remove("selected");
      }
    }
  }
};

// 在 document 级别监听键盘事件
onMounted(() => {
  document.addEventListener("keydown", handleKeyDown);
});

onUnmounted(() => {
  document.removeEventListener("keydown", handleKeyDown);
});
</script>

<style>
.quillContainer {
  width: 100%;
}

.ql-container.ql-snow {
  border-bottom-left-radius: 3px;
  border-bottom-right-radius: 3px;
}

.ql-toolbar.ql-snow {
  border-top-left-radius: 3px;
  border-top-right-radius: 3px;
}

#richContainer {
  height: 200px;
}

#richContainer .ql-editor.ql-blank::before {
  font-style: normal;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.25);
}

#richContainer .image-uploading {
  position: relative;
  display: inline-block;
}

#richContainer .image-uploading img {
  max-width: 98% !important;
  filter: blur(5px);
  opacity: 0.3;
}

#richContainer .image-uploading::before {
  content: "";
  box-sizing: border-box;
  position: absolute;
  top: 50%;
  left: 50%;
  width: 30px;
  height: 30px;
  margin-top: -15px;
  margin-left: -15px;
  border-radius: 50%;
  border: 3px solid #ccc;
  border-top-color: #1e986c;
  z-index: 1;
  animation: richSpinner 0.6s linear infinite;
}

@keyframes richSpinner {
  to {
    transform: rotate(360deg);
  }
}

.ql-snow a {
  color: #006eff;
}

/* 编辑超链接时样式 */
#richContainer .ql-tooltip::before {
  content: "";
}

#richContainer.richZh .ql-tooltip a.ql-action::after {
  content: "编辑";
}

#richContainer.richEn .ql-tooltip a.ql-action::after {
  content: "Edit";
}

#richContainer.richZh .ql-tooltip.ql-editing a.ql-action::after {
  content: "保存";
  display: inline-block;
}

#richContainer.richEn .ql-tooltip.ql-editing a.ql-action::after {
  content: "Save";
  display: inline-block;
}

.ql-snow .ql-tooltip.ql-editing input:focus {
  border: 1px solid #006eff;
  outline: none;
  border-radius: 3px;
}

#richContainer.richZh .ql-tooltip a.ql-remove::before {
  content: "移除";
}

#richContainer.richEn .ql-tooltip a.ql-remove::before {
  content: "Remove";
}

/* 自定义视频按钮颜色 */
.ql-toolbar button.ql-video-custom .ql-stroke {
  stroke: #929cb2 !important;
}

.ql-toolbar button.ql-video-custom .ql-fill {
  fill: #929cb2 !important;
}

/* 视频选中状态样式 */
#richContainer .ql-editor video {
  outline: 2px solid transparent;
  outline-offset: 2px;
  transition: outline-color 0.2s;
}

#richContainer .ql-editor video:focus,
#richContainer .ql-editor video.selected {
  outline-color: #006eff;
}
</style>
